import { UAParser } from 'ua-parser-js'
import { Container, Plugins } from '@getgo/container-client'
import { getShellLogger } from '../../common/logger'
import { environment, LocalStorageKeys } from '../../environments'
import { getFromLocalStorage, setToLocalStorage } from '../../common/dom-helpers'
import type { BrandingLDMandatoryMembers, LaunchDarklyPayload } from './models'
import { LaunchDarklyVariations, BrandVariationKeys } from './models'
import { getAllLaunchDarklyFlags } from './service'
import { isInternal, Product } from './utils'

/**
 * Returns launchdarkly flags if there are any otherwise undefined
 * @example
 *  const flags = window.shell.featureFlags.getLaunchDarklyFlags()
 *  if (!flags) {
 *   // handle me
 *  }
 */
export const getLaunchDarklyFlags = <T = LaunchDarklyPayload>() => {
  const values = getFromLocalStorage(LocalStorageKeys.goto_launch_darkly)

  if (!values) {
    return undefined
  }
  const launchDarklyValues = JSON.parse(values) as T

  const overrideValues = getFromLocalStorage(LocalStorageKeys.goto_launch_darkly_override)
  const launchDarklyOverrideValues: Partial<T> = overrideValues ? JSON.parse(overrideValues) : {}
  const mergedLaunchDarklyValues = mergeFlags<T>(launchDarklyValues, launchDarklyOverrideValues)
  return mergedLaunchDarklyValues
}

export const getLaunchDarklyFlag = <U extends keyof T, T = LaunchDarklyPayload>(flagName: U) =>
  getLaunchDarklyFlags<T>()?.[flagName]

export const setLaunchDarklyFlags = <T = LaunchDarklyPayload>(flags: T | null) =>
  setToLocalStorage(LocalStorageKeys.goto_launch_darkly, JSON.stringify(flags ?? ''))

/**
 * This is the branding default values.
 * This is useful in situations where a URL for an image is unreachable so we need to fall back to the URL of the image that is bundled with the app.
 */
export const brandDefaultValues: BrandingLDMandatoryMembers = {
  [BrandVariationKeys.FAVICON_ID]: 'goto/favicon.ico',
  [BrandVariationKeys.HOME_PAGE_LOGO_ID]: 'goto/darkmode/gotoLogo.svg',
  [BrandVariationKeys.JIVE_BRAND_NAME]: 'GoTo',
  [BrandVariationKeys.LOADING_LOGO_ID]: 'goto/loadingLogo.png',
  [BrandVariationKeys.ONBOARDING_COMPUTER_CALENDAR_ID]: 'gotoRebrand/onboardingComputerCalendar.svg',
  [BrandVariationKeys.ONBOARDING_COMPUTER_ID]: 'goto/onboardingComputer.svg',
  [BrandVariationKeys.SHELL_BRAND_NAME]: 'GoTo',
  [BrandVariationKeys.TOPBAR_LOGO_ID]: 'goto/gotoLogo.svg',
  [BrandVariationKeys.WELCOME_LOGO_ID]: 'goto/gotoLogo.svg',
  [BrandVariationKeys.CHAMELEON_THEME_PROVIDER_BRAND]: 'rebranding2021',
}

export const DEFAULT_FLAGS: LaunchDarklyPayload = {
  [LaunchDarklyVariations.PKCE_REFRESH_TOKENS]: {
    enabled: false,
    maxRefreshTokens: 0,
  },
  [LaunchDarklyVariations.SHELL_ENABLE_DARKMODE]: false,
  [LaunchDarklyVariations.BRANDING]: { ...brandDefaultValues },
  [LaunchDarklyVariations.SHELL_APPLICATION_SWITCHER]: false,
  [LaunchDarklyVariations.SHELL_NOTIFICATION_SETTINGS]: false,
  [LaunchDarklyVariations.SHELL_AVATAR_POPOVER_HELP_MENU_ITEM]: false,
  [LaunchDarklyVariations.SHELL_AVATAR_POPOVER_DND_MENU_ITEM]: false,
  [LaunchDarklyVariations.SHELL_HOTKEY_SETTINGS]: false,
  [LaunchDarklyVariations.SHELL_LANGUAGE_SETTINGS]: false,
  [LaunchDarklyVariations.SHELL_APP_LAUNCH_SETTINGS]: false,
  [LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE]: false,
  [LaunchDarklyVariations.SHELL_ADVANCED_PRESENCE_BUSY]: false,
  [LaunchDarklyVariations.SHELL_TOPBAR_ACCOUNT_SWITCHER]: false,
  [LaunchDarklyVariations.SHELL_GLOBAL_SEARCH]: false,
  [LaunchDarklyVariations.SHELL_CHAMELEON_NAVRAIL]: false,
  [LaunchDarklyVariations.SHELL_RESOLVE_USER_ACCESS]: false,
  [LaunchDarklyVariations.SHELL_RESOLVE_USER_ACCESS_ATTACHMENT]: false,
  [LaunchDarklyVariations.SHELL_USE_EXTENSIONS]: false,
  [LaunchDarklyVariations.SHELL_KEYBOARD_SHORTCUT_EDITING]: false,
  [LaunchDarklyVariations.SHELL_KEYBOARD_GLOBAL_SHORTCUT_EDITING]: false,
  [LaunchDarklyVariations.SHELL_NOTIFICATION_CHANNEL_V2]: false,
  [LaunchDarklyVariations.SHELL_TRACK_NC_DISCONNECT_BANNER]: false,
  [LaunchDarklyVariations.SHELL_USE_GOTO_BASE_URL]: false,
  [LaunchDarklyVariations.SHELL_DOCK_BADGE]: false,
  [LaunchDarklyVariations.SHELL_ENABLE_TRIAL_BANNER]: false,
  [LaunchDarklyVariations.SHELL_STANDALONE_CONFIG]: false,
  [LaunchDarklyVariations.SHELL_EXTENSION_PREREQUISITE_RULES]: false,
  [LaunchDarklyVariations.CONTACTS_TEAM_FILTER_SHARED]: false,
  [LaunchDarklyVariations.CONTACTS_TEAM_USE_TMP_BFF]: false,
  [LaunchDarklyVariations.CONTACTS_TEAM_API]: false,
}

interface SetupLaunchDarklyFlagParams {
  readonly numberOfAccounts?: number
  readonly userName?: string
  readonly externalUserKey?: string
  readonly accountKey?: string
  readonly pbxId?: string
  readonly entitlements?: readonly string[]
  readonly licenses?: readonly GoToLicense[]
  readonly allAccountKeys?: readonly string[]
}

export interface GoToLicense {
  readonly channel: string
  readonly description: string
  readonly enabled: boolean
  readonly key: string
  readonly products: readonly string[]
  readonly type: string
  readonly accountKey: string
}

/**
 * Calls LaunchDarkly's API and stores the flags using setLaunchDarklyFlags
 * If, for any reason, the call fails, then it will use the default values for the flags
 */
export const setupLaunchDarklyFlags = async ({
  userName,
  externalUserKey,
  accountKey,
  pbxId,
  entitlements,
  licenses,
  numberOfAccounts,
  allAccountKeys,
}: SetupLaunchDarklyFlagParams) => {
  performance.mark('launchDarklySetup start')
  let flags = mergeFlags({ ...DEFAULT_FLAGS }, getLaunchDarklyFlags())
  const uaParser = new UAParser()
  let { name: deviceBrand, version: deviceModel } = uaParser.getBrowser()
  const os = uaParser.getOS()

  try {
    if (Container.isPluginAvailable('ContainerInfo')) {
      const { appVersion, name } = await Plugins.ContainerInfo.getInfo()
      deviceBrand = name
      deviceModel = appVersion
    }
  } catch (e) {
    getShellLogger().error('Container Plugins caused an error', e)
  }

  try {
    const optionalValues = {
      externalUserKey,
      pbxId,
      accountKey,
      hostname: location.hostname,
      pathname: location.pathname,
      product: Product.WEB,
      deviceBrand,
      deviceModel,
      OSName: os.name,
      OSVersion: os.version,
      entitlements,
      licenses,
      numberOfAccounts,
      allAccountKeys,
    }
    const LDValues: { readonly [key: string]: string | readonly string[] } = Object.entries(optionalValues).reduce(
      (values: { [key: string]: string | readonly string[] }, value) => {
        if (value[1]) {
          values[value[0]] = String(value[1])
        }
        return values
      },
      {},
    )
    /**
     * Grabbing fresh flags every time since they may have been turned off since last fetch
     */
    const newFlags = await getAllLaunchDarklyFlags<LaunchDarklyPayload>(environment().launchDarklyClientId, {
      anonymous: true,
      custom: { ...LDValues, internal: isInternal(userName) },
      key: accountKey, // Will need to be changed to PBX ID later on to match with GTC
    })
    flags = mergeFlags(flags, newFlags)
  } catch (e) {
    getShellLogger().error('feature flags error', e)
  }

  setLaunchDarklyFlags(flags)
  performance.measure('launchDarklySetup', 'launchDarklySetup start')
}

// Fetch the current flags value for logout users. Called before the auth of the user.
// Those flags will be valid only for the environnment value.
// The user's specific flags are fetched after the auth.
export const setupAnonymousLaunchDarklyFlags = async () => {
  performance.mark('setupAnonymousLaunchDarklyFlags start')
  let flags = { ...DEFAULT_FLAGS }
  try {
    const newFlags = await getAllLaunchDarklyFlags<LaunchDarklyPayload>(environment().launchDarklyClientId, {
      anonymous: true,
    })
    flags = mergeFlags(flags, newFlags)
  } catch (e) {
    getShellLogger().error(' unsigned feature flags error', e)
  }
  setLaunchDarklyFlags(flags)
  performance.measure('setupAnonymousLaunchDarklyFlags', 'setupAnonymousLaunchDarklyFlags start')
}

const mergeObjects = <T>(original: T, overrides: Partial<T> | null | undefined): T => {
  const result: T = { ...original }
  if (overrides) {
    for (const key in overrides) {
      try {
        if (!!original && typeof original[key] === 'object' && typeof overrides[key] === 'object') {
          result[key] = mergeObjects(result[key], (overrides as T)[key])
        } else {
          result[key] = (overrides as T)[key]
        }
      } catch (e) {
        getShellLogger().error('merge error', e)
      }
    }
  }
  return result
}

const mergeFlags = <T = LaunchDarklyPayload>(original: T, overrides: Partial<T> | null | undefined): T =>
  mergeObjects<T>(original, overrides)

export const shellFeatureFlagsAPI = {
  getLaunchDarklyFlags,
  getLaunchDarklyFlag,
}
